import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, GraduationCap, LogOut, CheckCircle2, KeyRound, Clock } from "lucide-react";
import { toast } from "sonner";

const TOKEN_KEY = "eim_ih_token";

const ihApi = axios.create({ baseURL: API_BASE });
ihApi.interceptors.request.use((c) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

function CaseRow({ c, onDone }) {
  const [hasBeca, setHasBeca] = useState("");
  const [pct, setPct] = useState("");
  const [note, setNote] = useState(c.note || "");
  const [saving, setSaving] = useState(false);
  const responded = c.status === "responded";
  const inReview = c.status === "in_review";

  const submit = async () => {
    if (hasBeca === "") { toast.error("Indica si tiene beca o déjalo en revisión."); return; }
    setSaving(true);
    try {
      await ihApi.post(`/ih/cases/${c.id}/respond`, {
        has_scholarship: hasBeca === "si",
        percentage: hasBeca === "si" ? Number(pct) : 0,
        in_review: hasBeca === "revision",
        note: hasBeca === "revision" ? (note || null) : null,
      });
      toast.success(hasBeca === "revision" ? "Caso dejado en revisión." : "Respuesta registrada.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-xl p-4 bg-white" data-testid={`ih-case-${c.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="font-semibold text-primary">{c.family_name} <span className="text-muted-foreground font-normal">· {c.human_id}</span></p>
          <p className="text-xs text-muted-foreground">Beca declarada por la familia</p>
        </div>
        {responded && (
          <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-semibold" data-testid={`ih-case-status-${c.id}`}>
            <CheckCircle2 className="w-4 h-4" /> {c.percentage > 0 ? `Confirmada ${c.percentage}%` : "Sin beca"}
          </span>
        )}
        {inReview && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-semibold" data-testid={`ih-case-inreview-${c.id}`}>
            <Clock className="w-3.5 h-3.5" /> En revisión Beca IH
          </span>
        )}
      </div>
      <ul className="text-sm text-slate-600 mb-3 list-disc pl-5">
        {(c.claimed_children || []).map((ch, i) => (
          <li key={i}>{ch.nombre || "—"}{ch.pct != null && ch.pct !== "" ? ` — declara ${ch.pct}%` : ""}</li>
        ))}
      </ul>
      {c.consent_text && (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-3" data-testid={`ih-case-consent-${c.id}`}>
          <span className="font-semibold">Autorización del socio</span>
          {c.consent_at ? ` · ${new Date(c.consent_at).toLocaleString("es-CL")}` : ""}
          <p className="mt-0.5 text-emerald-900/80">“{c.consent_text}”</p>
        </div>
      )}
      {inReview && c.note && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-3" data-testid={`ih-case-note-${c.id}`}>
          Nota: {c.note}
        </p>
      )}
      {!responded && (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">¿Tiene beca IH?</Label>
              <Select value={hasBeca} onValueChange={setHasBeca}>
                <SelectTrigger className="h-11 rounded-xl mt-1" data-testid={`ih-hasbeca-${c.id}`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="si">Sí, es correcto</SelectItem>
                  <SelectItem value="no">No / Sin beca</SelectItem>
                  <SelectItem value="revision">En revisión (esperar)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {hasBeca === "si" && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">% real de beca</Label>
                <Input type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} className="h-11 rounded-xl mt-1" data-testid={`ih-pct-${c.id}`} />
              </div>
            )}
            <Button onClick={submit} disabled={saving} className="h-11 rounded-xl" data-testid={`ih-save-${c.id}`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </div>
          {hasBeca === "revision" && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nota (opcional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: esperando confirmación del colegio…" className="rounded-xl mt-1" data-testid={`ih-note-${c.id}`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChangePwModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (next.length < 8) { toast.error("La nueva contraseña debe tener al menos 8 caracteres."); return; }
    if (next !== confirm) { toast.error("Las contraseñas no coinciden."); return; }
    setSaving(true);
    try {
      await ihApi.post("/ih/change-password", { current_password: current, new_password: next });
      toast.success("Contraseña actualizada.");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-5" data-testid="ih-changepw-modal">
      <div className="w-full max-w-md bg-white rounded-2xl border border-border p-6">
        <h3 className="text-lg font-bold text-primary mb-4">Cambiar contraseña</h3>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contraseña actual</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="h-11 rounded-xl mt-1" data-testid="ih-changepw-current" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nueva contraseña</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="h-11 rounded-xl mt-1" data-testid="ih-changepw-new" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Repite la nueva contraseña</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 rounded-xl mt-1" data-testid="ih-changepw-confirm" />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={onClose} data-testid="ih-changepw-cancel">Cancelar</Button>
          <Button className="flex-1 rounded-xl" onClick={save} disabled={saving} data-testid="ih-changepw-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function IHPortal() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await ihApi.get("/ih/cases");
      setCases(data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(null); }
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { if (token) load(); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await ihApi.post("/auth/ih-login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setCases(null); };

  const requestLink = async () => {
    if (!email) { toast.error("Escribe tu correo primero."); return; }
    try {
      await ihApi.post("/auth/ih-request-password", { email });
      toast.success("Si tu correo está registrado, te enviamos un enlace para crear tu contraseña.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-secondary/40 flex items-center justify-center px-5">
        <div className="w-full max-w-md bg-white rounded-2xl border border-border p-8 fade-in">
          <div className="flex items-center gap-2 mb-6">
            <GraduationCap className="w-7 h-7 text-accent" />
            <h1 className="text-xl font-bold text-primary">Portal Beca Instituto Hebreo</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-6">Acceso exclusivo para la asistente social del Instituto Hebreo.</p>
          <form onSubmit={login} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-600">Correo</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl mt-1.5" required data-testid="ih-login-email" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-600">Contraseña</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl mt-1.5" required data-testid="ih-login-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl" data-testid="ih-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ingresar"}
            </Button>
          </form>
          <button onClick={requestLink} className="mt-4 text-sm text-primary hover:underline w-full text-center" data-testid="ih-request-link">
            Primera vez o ¿olvidaste tu contraseña? Enviarme un enlace
          </button>
        </div>
      </div>
    );
  }

  const pending = (cases || []).filter((c) => c.status !== "responded");
  const done = (cases || []).filter((c) => c.status === "responded");

  return (
    <div className="min-h-screen bg-secondary/40">
      <header className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-accent" />
            <span className="font-bold text-primary">Portal Beca Instituto Hebreo</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setShowChangePw(true)} className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="ih-open-changepw">
              <KeyRound className="w-4 h-4" /> Cambiar contraseña
            </button>
            <button onClick={logout} className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="ih-logout">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>
      {showChangePw && <ChangePwModal onClose={() => setShowChangePw(false)} />}
      <main className="max-w-4xl mx-auto px-5 py-8 space-y-8">
        <section>
          <h2 className="text-lg font-semibold text-primary mb-1">Casos por revisar {pending.length > 0 && <span className="text-accent">({pending.length})</span>}</h2>
          <p className="text-sm text-muted-foreground mb-4">Familias que declararon tener beca del Instituto Hebreo. Confirma o corrige el porcentaje.</p>
          {cases === null ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
            : pending.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="ih-no-pending">No hay casos pendientes.</p>
            : <div className="space-y-3" data-testid="ih-pending-list">{pending.map((c) => <CaseRow key={c.id} c={c} onDone={load} />)}</div>}
        </section>
        {done.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-primary mb-4">Revisados</h2>
            <div className="space-y-3">{done.map((c) => <CaseRow key={c.id} c={c} onDone={load} />)}</div>
          </section>
        )}
      </main>
    </div>
  );
}
